import { useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, List, ListItemButton, ListItemText } from '@mui/material';

import { useMarketsNavigationItems } from '@/components/NavBar/hooks/useMarketsNavigationItems';
import Typography from '@/components/Typography';
import { spacing } from '@/constants/ui';
import StepButtons from '@/pages/Onboarding/OnboardingForm/Steps/StepButtons';

const DefaultMarket = ({
  market,
  setMarket,
  activeStep,
  setActiveStep
}: {
  market: string;
  setMarket: (v: string) => void;
  activeStep: number;
  setActiveStep: (v: number) => void;
}) => {
  const { menuItems: marketItems } = useMarketsNavigationItems();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const handleToggle = (key: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Box>
      <Box>
        <Typography
          size={24}
          sx={{ fontWeight: 'bold', mb: 1.5 }}
        >
          Choose your homepage...
        </Typography>
        <Typography
          size={16}
          sx={{ fontStyle: 'italic', mb: spacing['1.5'] }}
        >
          It's three clicks to change it later in settings
        </Typography>
      </Box>
      <List
        aria-label='market-selectlist'
        sx={{
          maxHeight: 'calc(6 * 60px)',
          overflowY: 'auto',
          mb: spacing['2']
        }}
      >
        {marketItems.map(subHeadingWithMarkets => {
          const isExpanded = expandedItems[subHeadingWithMarkets.name!] || false;
          const hasChildren =
            subHeadingWithMarkets.children && subHeadingWithMarkets.children.length > 0;
          let expandIconToRender = null;
          if (hasChildren) {
            expandIconToRender = isExpanded ? <ExpandLess /> : <ExpandMore />;
          }
          return (
            <Box key={subHeadingWithMarkets.name}>
              <ListItemButton
                selected={subHeadingWithMarkets.children?.map(m => m.id).includes(market)}
                sx={{
                  backgroundColor: 'rgba(248, 248, 248, 0.20)',
                  mb: 1,
                  '&.Mui-selected': {
                    border: '2px solid #E84E0F'
                  }
                }}
                onClick={() => {
                  if (hasChildren) {
                    handleToggle(subHeadingWithMarkets.name!);
                  } else {
                    console.error(
                      'Can not expand subHeading without children:',
                      subHeadingWithMarkets
                    );
                  }
                }}
              >
                <ListItemText primary={subHeadingWithMarkets.name} />
                {expandIconToRender}
              </ListItemButton>

              {hasChildren && (
                <Collapse
                  unmountOnExit
                  in={isExpanded}
                  timeout='auto'
                >
                  <List
                    disablePadding
                    component='div'
                    sx={{ pl: 4 }}
                  >
                    {subHeadingWithMarkets.children!.map(marketItem => (
                      <ListItemButton
                        key={marketItem.key}
                        selected={market === marketItem.id}
                        sx={{
                          backgroundColor: 'rgba(248, 248, 248, 0.10)',
                          mb: 1,
                          '&.Mui-selected': {
                            border: '2px solid #E84E0F'
                          }
                        }}
                        onClick={() => {
                          setMarket(marketItem.id!);
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              noWrap
                              sx={{
                                overflow: 'hidden',
                                display: 'inline-flex',
                                gap: '8px',
                                alignItems: 'center'
                              }}
                            >
                              {marketItem.name}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>
      <StepButtons
        activeStep={activeStep}
        nextDisabled={!market}
        onBackClick={() => {
          setActiveStep(activeStep - 1);
        }}
        onNextClick={() => {
          setActiveStep(activeStep + 1);
        }}
      />
    </Box>
  );
};

export default DefaultMarket;
